<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useActiveDeviceRenderStore } from "@/stores/activeDeviceRender";
import { useIssuesStore } from "@/stores/issues";
import PreviewScanError from "@/views/preview/PreviewScanError.vue";
import InnerIssueSection from "@/views/preview/sections/InnerIssueSection.vue";
import categoryMapping from "@/data/categoryMapping";
import categoryGrouping from "@/data/categoryGrouping";
import Spinner from "@/components/Spinner.vue";

const props = defineProps({
  issueFilter: {
    type: String,
    default: 'all'
  },
  filteredIssues: {
    type: Function,
    required: true
  },
  showBoxes: {
    type: Function,
    required: true
  },
  mcp: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['update:issueFilter']);

const activeDeviceRender = useActiveDeviceRenderStore();
const route = useRoute();
const issuesStore = useIssuesStore();

const {
  brokenLinkMarks,
  cloudFlareError,
  internalRedirectMarks,
  brokenImageMarks,
  missingAltTagImageMarks,
  brokenResources,
  consoleErrors,
  layoutErrors,
  contentErrorMissingFavicons,
  technicalMissingDescriptions,
  technicalMissingTitles,
} = storeToRefs(issuesStore);

function filterLayoutErrorsByPriorityAndDevice(priority, device) {
  let r = [];
  for (let layoutError of layoutErrors.value.filter(item => item.priority === priority)) {
    r.push(...layoutError.devices.filter(d => d.name === device));
  }
  return r;
}

function filterIssuesByPriority(priority, issues) {
  return issues.filter(item => item.priority === priority)
}

function filterIssuesBySeverity(severity) {
  const allIssues = issuesStore.allAuditIssues;
  return allIssues.filter((item) => {
    if (severity === 'all') {
      return true;
    }
    return item.priority === severity
  });
}

function updateIssueFilter(newFilter) {
  emit('update:issueFilter', newFilter);
}
</script>

<template>
  <div class="row p-2 mt-3" v-if="!mcp">

    <!-- Layout Checks -->
    <div class="col-md-6 ">
      <div class="audit-evidence audit-evidence-layout h-100">
        <div class="row text-center">

          <div class="col">
            <router-link :to="{params: {deviceId: 'chrome_1280'}, query: { 'section': 'audit'} }">
              <i class="fa fa-desktop device-icon pb-1 pt-2"></i>
              <div class="text-black" :class="{
                        'fw-bolder': activeDeviceRender.devicePreview.deviceName === 'Desktop',
                        'fw-medium': activeDeviceRender.devicePreview.deviceName !== 'Desktop',
                        }">Desktop
              </div>
              <div v-if="filterLayoutErrorsByPriorityAndDevice('high', 'Desktop').length > 0"
                   class="small status-danger" style="padding-top:2px">
                <i class="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div v-else-if="filterLayoutErrorsByPriorityAndDevice('medium', 'Desktop').length > 0"
                   class="small status-warn" style="padding-top:2px">
                <i class="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div v-else-if="filterLayoutErrorsByPriorityAndDevice('low', 'Desktop').length > 0"
                   class="small status-suggestion" style="padding-top:2px">
                <i class="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div v-else class="small status-pass" style="padding-top:2px">
                <i class="fa-solid fa-circle-check"></i>
              </div>
            </router-link>
          </div>

          <div class="col">
            <router-link :to="{params: {deviceId: 'ipad_pro_11'}, query: { 'section': 'audit'}}">
              <i class="fa-solid fa-tablet-screen-button  pb-1 pt-2 device-icon"></i>
              <div class="text-black" :class="{
                        'fw-bolder': activeDeviceRender.devicePreview.deviceName === 'Tablet',
                        'fw-medium': activeDeviceRender.devicePreview.deviceName !== 'Tablet',
                        }">Tablet
              </div>
              <div v-if="filterLayoutErrorsByPriorityAndDevice('high', 'Tablet').length > 0"
                   class="small status-danger" style="padding-top:2px">
                <i class="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div v-else-if="filterLayoutErrorsByPriorityAndDevice('medium', 'Tablet').length > 0"
                   class="small status-warn" style="padding-top:2px">
                <i class="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div v-else-if="filterLayoutErrorsByPriorityAndDevice('low', 'Tablet').length > 0"
                   class="small status-suggestion" style="padding-top:2px">
                <i class="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div v-else class="small status-pass" style="padding-top:2px">
                <i class="fa-solid fa-circle-check"></i>
              </div>
            </router-link>

          </div>

          <div class="col">
            <router-link :to="{params: {deviceId: 'iphone_14'}, query: { 'section': 'audit'}}">
              <i class="fa-solid fa-mobile-screen  pb-1 pt-2 device-icon"></i>
              <div class="text-black" :class="{
                        'fw-bolder': activeDeviceRender.devicePreview.deviceName === 'Mobile',
                        'fw-medium': activeDeviceRender.devicePreview.deviceName !== 'Mobile',
                        }">Mobile
              </div>


              <div v-if="filterLayoutErrorsByPriorityAndDevice('high', 'Mobile').length > 0"
                   class="small status-danger" style="padding-top:2px">
                <i class="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div v-else-if="filterLayoutErrorsByPriorityAndDevice('medium', 'Mobile').length > 0"
                   class="small status-warn" style="padding-top:2px">
                <i class="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div v-else-if="filterLayoutErrorsByPriorityAndDevice('low', 'Mobile').length > 0"
                   class="small status-suggestion" style="padding-top:2px">
                <i class="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div v-else class="small status-pass" style="padding-top:2px">
                <i class="fa-solid fa-circle-check"></i>
              </div>
            </router-link>
          </div>

        </div>
      </div>
    </div>

    <!-- Technical Checks -->
    <div class="col-md-6">
      <div class="audit-card audit-evidence h-100">
        <div class="d-flex align-items-center justify-content-between py-1 border-bottom">
          <div class="d-flex align-items-center gap-2">
            <i class="fa-solid fa-image"></i>
            <span>Images</span>
          </div>
          <span
              v-if="filterIssuesByPriority('high', [...brokenImageMarks, ...missingAltTagImageMarks]).length > 0"
              class="small status-danger">
                      <i class="fa-solid fa-circle-xmark"></i> {{
              filterIssuesByPriority('high', [...brokenImageMarks, ...missingAltTagImageMarks]).length
            }}</span>
          <span
              v-else-if="filterIssuesByPriority('medium', [...brokenImageMarks, ...missingAltTagImageMarks]).length > 0"
              class="small status-warn">
                      <i class="fa-solid fa-circle-xmark"></i> {{
              filterIssuesByPriority('medium', [...brokenImageMarks, ...missingAltTagImageMarks]).length
            }}</span>
          <span
              v-else-if="filterIssuesByPriority('low', [...brokenImageMarks, ...missingAltTagImageMarks]).length > 0"
              class="small status-suggestion">
                      <i class="fa-solid fa-circle-xmark"></i> {{
              filterIssuesByPriority('low', [...brokenImageMarks, ...missingAltTagImageMarks]).length
            }}</span>
          <span v-else class="small status-pass"><i class="fa-solid fa-circle-check"></i> Pass</span>
        </div>
        <div class="d-flex align-items-center justify-content-between py-1 border-bottom">
          <div class="d-flex align-items-center gap-2">
            <i class="fa-solid fa-link"></i>
            <span>Links</span>
          </div>
          <span
              v-if="filterIssuesByPriority('high', [...brokenLinkMarks, ...internalRedirectMarks]).length > 0"
              class="small status-danger">
                      <i class="fa-solid fa-circle-xmark"></i> {{
              filterIssuesByPriority('high', [...brokenLinkMarks, ...internalRedirectMarks]).length
            }}</span>
          <span
              v-else-if="filterIssuesByPriority('medium', [...brokenLinkMarks, ...internalRedirectMarks]).length > 0"
              class="small status-warn">
                      <i class="fa-solid fa-circle-xmark"></i>{{
              filterIssuesByPriority('medium', [...brokenLinkMarks, ...internalRedirectMarks]).length
            }}</span>
          <span
              v-else-if="filterIssuesByPriority('low', [...brokenLinkMarks, ...internalRedirectMarks]).length > 0"
              class="small status-suggestion">
                      <i class="fa-solid fa-circle-xmark"></i> {{
              filterIssuesByPriority('low', [...brokenLinkMarks, ...internalRedirectMarks]).length
            }}</span>
          <span v-else class="small status-pass"><i class="fa-solid fa-circle-check"></i> Pass</span>
        </div>

        <div class="d-flex align-items-center justify-content-between py-1 border-bottom">
          <div class="d-flex align-items-center gap-2">
            <i class="fa-solid fa-code"></i>
            <span>Structure</span>
          </div>
          <span
              v-if="filterIssuesByPriority('high', [...technicalMissingTitles, ...technicalMissingDescriptions, ...contentErrorMissingFavicons]).length > 0"
              class="small status-danger">
                      <i class="fa-solid fa-circle-xmark"></i> {{
              filterIssuesByPriority('high', [...technicalMissingTitles, ...technicalMissingDescriptions, ...contentErrorMissingFavicons]).length
            }}</span>
          <span
              v-else-if="filterIssuesByPriority('medium', [...technicalMissingTitles, ...technicalMissingDescriptions, ...contentErrorMissingFavicons]).length > 0"
              class="small status-warn">
                      <i class="fa-solid fa-circle-xmark"></i> {{
              filterIssuesByPriority('medium', [...technicalMissingTitles, ...technicalMissingDescriptions, ...contentErrorMissingFavicons]).length
            }}</span>
          <span v-else class="small status-pass"><i class="fa-solid fa-circle-check"></i> Pass</span>
        </div>

        <div class="d-flex align-items-center justify-content-between py-1">
          <div class="d-flex align-items-center gap-2">
            <i class="fa-solid fa-file-circle-xmark"></i>
            <span>Page Errors</span>
          </div>
          <span v-if="filterIssuesByPriority('high', [...brokenResources, ...consoleErrors]).length > 0"
                class="small status-danger">
                      <i class="fa-solid fa-circle-xmark"></i> {{
              filterIssuesByPriority('high', [...brokenResources, ...consoleErrors]).length
            }}</span>
          <span
              v-else-if="filterIssuesByPriority('medium', [...brokenResources, ...consoleErrors]).length > 0"
              class="small status-warn">
                      <i class="fa-solid fa-circle-xmark"></i> {{
              filterIssuesByPriority('medium', [...brokenResources, ...consoleErrors]).length
            }}</span>
          <span v-else class="small status-pass"><i class="fa-solid fa-circle-check"></i> Pass</span>
        </div>
      </div>
    </div>

  </div>
  <template v-if="['in_progress', 'completed'].includes(activeDeviceRender.scanState)">

    <div class="alert alert-danger" role="alert"
         v-if="!activeDeviceRender.page.httpStatusOkOrPending">
      <p class="mb-0"><b>Page Load Error</b><br> We could not load this page, the server responded with a
        status
        code of {{ activeDeviceRender.page.httpStatusCode }}. </p>
    </div>

    <div v-else class="audit-summary" style="font-size: 0.875em">


      <preview-scan-error v-if="activeDeviceRender.page.status === 'error'"/>

      <div v-if="['in_progress'].includes(activeDeviceRender.scanState) && filterIssuesBySeverity('all').length === 0">
        <div class="p-3 text-center">
          <div class="mb-0 mt-2">
            <div class="text-muted">
              <spinner></spinner>
              Looking for layout and technical issues.
            </div>
          </div>

          <div class="row g-2 text-start mt-0">
            <!-- AI Readiness specific loading -->

            <!-- Proofreading specific loading -->

              <div class="col-12">
                <div class="d-flex align-items-start gap-3 p-3 py-2 border rounded bg-light-subtle">
                  <div class="icon-shape rounded is-processing flex-shrink-0" :class="categoryGrouping.proofread.color"
                       style="--processing-color: #198754">
                    <i class="fa fa-tablet-screen-button"></i>
                  </div>
                  <div>
                    <h6 class="mb-1 fw-bold">Layout Checks</h6>
                    <p class="mb-0 text-muted small">Checking Mobile, Desktop and Tablet views for layout issues.</p>
                  </div>
                </div>
              </div>

              <div class="col-12">
                <div class="d-flex align-items-start gap-3 p-3 py-2 border rounded bg-light-subtle">
                  <div class="icon-shape rounded is-processing flex-shrink-0" :class="categoryGrouping.integrity.color"
                       style="--processing-color: #0dcaf0">
                    <i class="fa fa fa-pen-fancy"></i>
                  </div>
                  <div>
                    <h6 class="mb-1 fw-bold">Technical Checks</h6>
                    <p class="mb-0 text-muted small">Looking for console errors, image issues and broken links.</p>
                  </div>
                </div>
              </div>

              <div class="col-12">
                <div class="d-flex align-items-start gap-3 p-3 py-2 border rounded bg-light-subtle">
                  <div class="icon-shape rounded is-processing flex-shrink-0" :class="categoryGrouping.proofread.color"
                       style="--processing-color: #198754">
                    <i class="fa fa-code"></i>
                  </div>
                  <div>
                    <h6 class="mb-1 fw-bold">Page Structure</h6>
                    <p class="mb-0 text-muted small">Evaluates page titles, descriptions and SEO related elements.</p>
                  </div>
                </div>
              </div>
          </div>

          <div class="mt-4 text-muted small italic">
            <i class="fa fa-circle-notch fa-spin"></i> This usually takes less than a minute.
          </div>
        </div>
      </div>


      <div v-if="cloudFlareError" class="alert alert-danger mt-2">
        <div><span class="fa fa-warning"></span> <b>We had some issues checking this page</b></div>
        <div class="ms-2 mt-1">Cloudflare is blocking our requests. Please see instructions on how to fix
          this
          issue.
        </div>
      </div>

      <div v-if="filterIssuesBySeverity('all').length > 0">
        <div class="d-flex justify-content-between align-items-center mb-0 mt-2 p-2 border-bottom">
          <div class="h6 mb-0"><b>{{ filterIssuesBySeverity('all').length }}</b> Issues Found</div>
          <div class="d-flex gap-2">
                      <span v-if="filterIssuesBySeverity('high').length > 0"
                            class="badge badge-danger clickable"
                            :class="{'active-priority': issueFilter === 'high'}"
                            @click.prevent="updateIssueFilter(issueFilter === 'high' ? 'all' : 'high'); showBoxes(false)">
                        {{ filterIssuesBySeverity('high').length }} High
                      </span>
            <span v-if="filterIssuesBySeverity('medium').length > 0"
                  class="badge badge-warning clickable"
                  :class="{'active-priority': issueFilter === 'medium'}"
                  @click.prevent="updateIssueFilter(issueFilter === 'medium' ? 'all' : 'medium'); showBoxes(false);">
                        {{ filterIssuesBySeverity('medium').length }} Med
                      </span>
            <span v-if="filterIssuesBySeverity('low').length > 0"
                  class="badge badge-info clickable"
                  :class="{'active-priority': issueFilter === 'low'}"
                  @click.prevent="updateIssueFilter(issueFilter === 'low' ? 'all' : 'low'); showBoxes(false)">
                        {{ filterIssuesBySeverity('low').length }} Low
                      </span>
          </div>
        </div>
      </div>
      <div v-else-if="activeDeviceRender.scanState === 'completed'">
        <div class="p-4 m-2 bg-success-subtle border border-success border-opacity-10 rounded-3 mb-3 text-center">
          <div class="mb-2">
            <span class="fa fa-circle-check text-success fs-2"></span>
          </div>
          <h5 class="fw-bold text-success-emphasis mb-2">No Issues Detected</h5>
          <p class="text-muted small mb-0">
            Your page looks good across <b>mobile, tablet, and desktop</b> views with no technical issues.
          </p>
        </div>

      </div>

      <ul class="list-group list-group-flush ">
        <div class="list-group" id="id-audit-issue-list">
          <!-- @formatter:off -->
            <inner-issue-section icon="fa fa-tablet-screen-button" id="aspect_ratio" :title="categoryMapping.layout['aspect_ratio'].label" :static-title="true" :issues="filteredIssues(layoutErrors.filter(e=>e.reason === 'aspect_ratio'))"></inner-issue-section>
            <inner-issue-section id="text_overlap" :title="categoryMapping.layout['text_overlap'].label" :static-title="true" :issues="filteredIssues(layoutErrors.filter(e=>e.reason === 'text_overlap'))"></inner-issue-section>
            <inner-issue-section id="button_overlap" :title="categoryMapping.layout['button_overlap'].label" :static-title="true" :issues="filteredIssues(layoutErrors.filter(e=>e.reason === 'button_overlap'))"></inner-issue-section>
            <inner-issue-section id="narrow" :title="categoryMapping.layout['narrow'].label" :static-title="true" :issues="filteredIssues(layoutErrors.filter(e=>e.reason === 'narrow'))"></inner-issue-section>
            <inner-issue-section id="lsep" :title="categoryMapping.layout['lsep'].label" :static-title="true" :issues="filteredIssues(layoutErrors.filter(e=>e.reason === 'lsep'))"></inner-issue-section>
            <inner-issue-section id="etx" :title="categoryMapping.layout['etx'].label" :static-title="true" :issues="filteredIssues(layoutErrors.filter(e=>e.reason === 'etx'))"></inner-issue-section>
            <inner-issue-section id="unsupported_unicode" :title="categoryMapping.layout['unsupported_unicode'].label" :static-title="true" :issues="filteredIssues(layoutErrors.filter(e=>e.reason === 'unsupported_unicode'))"></inner-issue-section>
            <inner-issue-section id="entity" :title="categoryMapping.layout['entity'].label" :static-title="true" :issues="filteredIssues(layoutErrors.filter(e=>e.reason === 'entity'))"></inner-issue-section>
            <inner-issue-section id="horizontal_scrolling" :title="categoryMapping.layout['horizontal_scrolling'].label" :static-title="true" :issues="filteredIssues(layoutErrors.filter(e=>e.reason === 'horizontal_scrolling'))"></inner-issue-section>
            <inner-issue-section icon="fa fa-tablet-screen-button" id="overflowing_container" :title="categoryMapping.layout['overflowing_container'].label" :static-title="true" :issues="filteredIssues(layoutErrors.filter(e=>e.reason === 'overflowing_container'))"></inner-issue-section>
            <inner-issue-section id="outside_window" :title="categoryMapping.layout['outside_window'].label" :static-title="true" :issues="filteredIssues(layoutErrors.filter(e=>e.reason === 'outside_window'))"></inner-issue-section>
            <inner-issue-section icon="fa fa-link" :issues="filteredIssues(brokenLinkMarks)" id="broken_link"  title="Broken Link"/>
            <inner-issue-section icon="fa-solid fa-image" :issues="filteredIssues(brokenImageMarks)" id="broken_image" title="Broken Image"/>
            <inner-issue-section icon="fa fa-code"  :issues="filteredIssues(contentErrorMissingFavicons)" id="missing_favicon" title="Missing Favicon"/>
            <inner-issue-section icon="fa fa-code"  :issues="filteredIssues(technicalMissingTitles)" id="missing_title" title="Missing Title"/>
            <inner-issue-section icon="fa fa-code" :issues="filteredIssues(technicalMissingDescriptions)" id="missing_description" title="Missing Meta Description" :static-title="true"/>
            <inner-issue-section icon="fa-solid fa-image" :issues="filteredIssues(missingAltTagImageMarks)" id="missing_alt"  title="Missing Alt Tag"/>
            <inner-issue-section icon="fa-solid fa-terminal" :issues="filteredIssues(consoleErrors)" id="console_error" title="Console Error"/>
            <inner-issue-section icon="fa-solid fa-file-circle-xmark" :issues="filteredIssues(brokenResources)" id="broken_resource"  title="Broken Resource"/>
            <inner-issue-section icon="fa fa-link" :issues="filteredIssues(internalRedirectMarks)" id="internal_redirect" title="Internal Redirect"/>
            <!-- @formatter:on -->
        </div>

      </ul>

    </div>
  </template>
</template>

<style scoped>
.bg-light-subtle {
  background-color: rgba(13, 110, 253, 0.1);
}

.bg-soft-success {
  background-color: rgba(25, 135, 84, 0.1);
}

.bg-light-subtle {
  background-color: rgba(13, 202, 240, 0.1);
}


.audit-evidence {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fbfdff;
  padding: 18px 6px 0px;
  margin-bottom: 4px;
  position: relative;
  font-size: 12px;
}

.audit-evidence::before {
  content: "TECHNICAL CHECKS";
  position: absolute;
  top: -9px;
  left: 12px;
  background: #ffffff;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .04em;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.audit-evidence.audit-evidence-layout::before {
  content: "LAYOUT CHECKS";
}

.status-pass {
  color: #16a34a;
}

.status-suggestion {
  color: gray;
}

.status-warn {
  color: orange;
}

.status-danger {
  color: #ea580c
}

.device-icon {
  font-size: 28px;
  margin-bottom: 0.5rem;
  color: #374151;
}
</style>
